import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Compass, Tent, Sparkles, Sun, Facebook, Instagram, Flame, Clock } from "lucide-react";
import { api } from "../lib/api";
import { SOCIAL } from "../lib/social";

/**
 * /share/final-call — ONE bold 9:16 reel for the absolute final pre-launch
 * push. Designed to be screen-recorded and posted as a single high-impact
 * Reel / TikTok / Story.
 *
 *   • Bright sunny camping hero — yellow + sky-blue gradient over a golden-
 *     hour campsite photo so the post stops the scroll.
 *   • Single giant headline + snappy sub-line (no scroll between scenes).
 *   • Live "X days left" countdown auto-computed from LAUNCH_DATE.
 *   • Big white QR code lands users on /register in one scan.
 *   • Bottom row = TWO clean follow buttons (Facebook + Instagram) so the
 *     reel doubles as a follow-growth post even for viewers who aren't
 *     ready to sign up yet.
 *   • Caption pack underneath the reel for one-tap copy/paste.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const SIGNUP_URL = `${PUBLIC_URL}/register?ref=final-call-reel`;
const LAUNCH_DATE = new Date("2026-05-31T00:00:00Z");

// One bright, sunny golden-hour camping photo (Pexels — free for commercial use).
const HERO_PHOTO =
  "https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=1080&h=1920&fit=crop";

function Sticker({ children, color = "#ff4500", textColor = "#ffffff" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

export default function FinalCallReel() {
  const [count, setCount] = useState(22000);

  useEffect(() => {
    let alive = true;
    try {
      api
        .get("/campsites/stats")
        .then(({ data }) => {
          if (alive && data?.total) setCount(data.total);
        })
        .catch(() => {});
    } catch {
      /* api may be unavailable — keep the fallback number */
    }
    return () => {
      alive = false;
    };
  }, []);

  const daysLeft = useMemo(() => {
    const ms = LAUNCH_DATE.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  }, []);

  const fmt = useMemo(() => count.toLocaleString("en-GB"), [count]);

  // Snappy caption variants for copy/paste.
  const captions = useMemo(
    () => [
      {
        title: "Hook · Last call (snappiest)",
        body: `Last call before launch. ⛺🔥\n\n${daysLeft} days. ${fmt} UK campsites. One map.\n\nSign up free in 20 seconds → you'll be ready the second we go live on 31 May.\n\n🔗 camp-compass.uk/register\n👉 Follow ${SOCIAL.instagram.handle} for launch perks\n\n#campinguk #ukcamping #campsite #countdown #launchday #campervan #bankholidayweekend`,
      },
      {
        title: "Hook · FOMO (bank holiday angle)",
        body: `Bank holiday + ${fmt} UK pitches + ONE map = no more 30 browser tabs. 🗺️⛺\n\nWe're live in ${daysLeft} days. Sign up TODAY → on launch morning you're already booking while everyone else is still creating accounts.\n\nFree. 20 seconds. No app store. Tap the link.\n\n🔗 camp-compass.uk/register\n👉 Follow us for first-look campsite drops\n\n#bankholiday #ukcamping #campervanlife #lastminutebooking #ukstaycation #campingfomo`,
      },
      {
        title: "Hook · Dream sale (sunny optimism)",
        body: `Lakes. Coast. Highlands. Forest. Farm. Pods. Bothies. 🌅\n\n${fmt} UK campsites — all on one map, in your pocket, in ${daysLeft} days.\n\nFilter by what matters: price · dogs · hot showers · kids · EHU · year-round.\n\nSign up free, be first in.\n\n🔗 camp-compass.uk/register\n👉 ${SOCIAL.instagram.handle} on Instagram · ${SOCIAL.facebook.handle} on Facebook\n\n#campinguk #ukcamping #campsite #dreamcamping #campingfamily #ukstaycation`,
      },
    ],
    [daysLeft, fmt],
  );

  return (
    <div className="bg-[#0e0e0e] min-h-screen text-white" data-testid="final-call-page">
      <header className="max-w-3xl mx-auto px-5 py-8">
        <div className="eyebrow text-[#ff4500] mb-2 flex items-center gap-2">
          <Flame className="w-3.5 h-3.5" /> Final Call · {daysLeft} days to go
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">
          One last reel.<br />Sign up. Follow. Done.
        </h1>
        <p className="text-sm text-[#bbb] leading-relaxed max-w-xl">
          A single bright, scroll-stopping 9:16 reel built for the absolute final push
          before <strong>31 May 2026</strong>. Screen-record this scene, post tonight or
          tomorrow morning. The QR lands users on the signup form; the follow buttons
          turn viewers who aren't ready to sign up into Instagram & Facebook followers.
        </p>
      </header>

      {/* THE REEL — single 9:16 scene */}
      <div className="flex flex-col items-center gap-6 px-3 pb-10">
        <section
          className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden"
          style={{
            aspectRatio: "9 / 16",
            maxWidth: 540,
            backgroundImage: `linear-gradient(180deg, rgba(255,180,73,0.25) 0%, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.78) 100%), url("${HERO_PHOTO}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid="final-call-scene"
        >
          {/* TOP — sticker + countdown */}
          <div className="p-6 sm:p-8 pt-10 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <Sticker color="#ffffff" textColor="#0e0e0e">
                <Sun className="w-3.5 h-3.5" /> Final call
              </Sticker>
              <Sticker color="#ff4500">
                <Clock className="w-3.5 h-3.5" /> {daysLeft}d left
              </Sticker>
            </div>
          </div>

          {/* MIDDLE — giant headline */}
          <div className="px-6 sm:px-8 text-center">
            <div className="font-display text-[68px] sm:text-[96px] leading-[0.92] mb-3 drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]">
              LAST<br />CALL.
            </div>
            <div
              className="inline-block font-display text-2xl sm:text-3xl px-3 py-1 mb-4"
              style={{ background: "#ff4500", color: "#fff" }}
            >
              {daysLeft} DAYS TO LAUNCH
            </div>
            <div className="text-lg sm:text-2xl font-bold leading-snug text-white drop-shadow mb-2">
              {fmt} UK campsites.
            </div>
            <div className="text-base sm:text-xl font-bold leading-snug text-[#ffe7d6] drop-shadow">
              One map. Free. 20 seconds.
            </div>
          </div>

          {/* BOTTOM — Sign up CTA + QR + follow buttons */}
          <div
            className="p-5 sm:p-6 space-y-4"
            style={{
              background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.78) 100%)",
            }}
          >
            <div className="text-center font-display text-[44px] sm:text-[58px] leading-none drop-shadow-lg text-white">
              SIGN UP FREE
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="text-left">
                <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/90 mb-1">
                  Scan or visit
                </div>
                <div className="font-display text-base sm:text-lg leading-tight">
                  camp-compass.uk
                  <br />
                  /register
                </div>
              </div>
              <div className="bg-white p-1.5 shrink-0" data-testid="final-call-qr">
                <QRCodeSVG
                  value={SIGNUP_URL}
                  size={104}
                  bgColor="#ffffff"
                  fgColor="#111111"
                  includeMargin={false}
                />
              </div>
            </div>

            {/* Follow buttons row */}
            <div className="pt-2">
              <div className="text-center text-[10px] mono uppercase tracking-[0.25em] text-white/85 mb-2">
                Or follow for launch updates
              </div>
              <div className="flex items-center justify-center gap-2">
                <a
                  href={SOCIAL.facebook.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="final-call-fb"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-white text-xs font-bold mono uppercase tracking-[0.14em]"
                  style={{ background: "#1877f2" }}
                >
                  <Facebook className="w-4 h-4" /> Facebook
                </a>
                <a
                  href={SOCIAL.instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="final-call-ig"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-white text-xs font-bold mono uppercase tracking-[0.14em]"
                  style={{
                    background: "linear-gradient(135deg,#fa7e1e 0%,#d62976 50%,#962fbf 100%)",
                  }}
                >
                  <Instagram className="w-4 h-4" /> Instagram
                </a>
              </div>
            </div>
          </div>
          <div
            className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/65 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-bold mono uppercase tracking-[0.22em] text-center pointer-events-none z-10"
            aria-hidden="true"
          >
            🌐 camp-compass.uk
          </div>
        </section>

        <div className="text-center text-xs text-[#888] mono uppercase tracking-[0.2em] max-w-md">
          Screen-record this scene on your phone (1080×1920) or in a 540×960 desktop window.
        </div>
      </div>

      {/* CAPTION PACK */}
      <section className="max-w-3xl mx-auto px-5 pb-20 space-y-6" data-testid="final-call-captions">
        <div className="border-l-2 border-[#ff4500] pl-4">
          <h2 className="font-display text-2xl mb-2">Snappy caption pack</h2>
          <p className="text-sm text-[#bbb]">
            Three caption variants. Pick the one that fits the moment (last call · bank holiday · dream sale)
            and post tonight or tomorrow morning for max momentum into launch day.
          </p>
        </div>

        {captions.map((c, i) => (
          <article
            key={i}
            className="p-5 border hairline bg-[#1a1a1a]"
            data-testid={`final-call-caption-${i + 1}`}
          >
            <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500] mb-2">
              {c.title}
            </div>
            <pre className="bg-[#0e0e0e] p-3 mb-3 whitespace-pre-wrap text-sm leading-relaxed font-sans">
              {c.body}
            </pre>
            <button
              type="button"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(c.body);
                } catch {
                  /* clipboard may be unavailable in some browsers */
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#ff4500] hover:bg-[#e03e00] text-white text-xs font-bold mono uppercase tracking-[0.14em] transition-colors"
              data-testid={`final-call-copy-${i + 1}`}
            >
              <Sparkles className="w-3.5 h-3.5" /> Copy caption
            </button>
          </article>
        ))}

        <div className="p-5 border-2 border-[#ff4500] bg-[#1a1a1a]" data-testid="final-call-tips">
          <h3 className="font-display text-xl mb-2 flex items-center gap-2">
            <Tent className="w-5 h-5" strokeWidth={1.7} /> How to use this reel
          </h3>
          <ol className="space-y-2 text-sm text-[#ddd] leading-relaxed list-decimal pl-5">
            <li>Open this page on your phone (or a 540×960 desktop window).</li>
            <li>Start your screen recorder. Hold on the reel scene for ~5-8 seconds.</li>
            <li>Trim in the Instagram / TikTok / Reels editor — add a trending sunny acoustic sound.</li>
            <li>Paste one of the captions above. Post tonight 6-8pm or tomorrow 11am.</li>
            <li>Pin the comment with your direct link: <strong>camp-compass.uk/register</strong></li>
          </ol>
          <div className="mono text-[10px] text-[#888] mt-4 leading-snug border-t hairline pt-3">
            <Compass className="w-3 h-3 inline-block mr-1" /> Goal: bank a final wave of signups AND
            grow follows so launch-day posts reach a warm audience.
          </div>
        </div>
      </section>
    </div>
  );
}
